import { Link } from "react-router-dom";
import { ArrowRight, Shield } from "lucide-react";
import Reveal from "@/components/site/Reveal";

/**
 * Editorial "diptych" marketplace — 2 magazine-style columns, no SaaS boxes.
 * Left: for Pflegekräfte · Right: for Einrichtungen.
 */

const MARKET = [
  {
    id: "pflegekraft",
    kicker: "Für Pflegekräfte · Kostenlos",
    number: "Nº 01",
    headline: "Suchst du einen neuen Job?",
    lede:
      "Lege ein kurzes Profil an — was du gelernt hast, in welcher Region du arbeiten willst, ab wann du verfügbar bist. Pflegedienste und Heime melden sich direkt bei dir.",
    benefits: [
      "Kein Headhunter-Aufschlag · keine Provision",
      "Kontaktdaten bleiben in deiner Hand",
      "Profil jederzeit deaktivieren oder löschen",
      "Vertraglicher Abwerbeschutz — Mitgliederpflicht",
      "Plus: alle Schatzkiste-Vorlagen inkl.",
    ],
    ctaHref: "/preise",
    ctaLabel: "Mitglied werden",
    ctaTestId: "marketplace-cta-pflegekraft",
    cardTestId: "marketplace-card-pflegekraft",
    accent: "terra",
  },
  {
    id: "einrichtung",
    kicker: "Für Einrichtungen · ab Single-Held",
    number: "Nº 02",
    headline: "Wir suchen Personal — sofort.",
    lede:
      "Offene Stelle direkt im Campus ausschreiben. Frisch ausgebildete Pflegekräfte aus unserem Bildungszentrum sind hier registriert — LG1, LG2, examinierte PFK, Praxisanleiter, PDLs.",
    benefits: [
      "Direkter Bewerber-Kontakt · kein Drittanbieter",
      "Zugriff auf Helden-Absolventen-Pool",
      "Vertraglich vereinbarter Abwerbeschutz zwischen Einrichtungen",
      "Pflichtfortbildungen für 5 Köpfe inklusive",
    ],
    ctaHref: "/campus/pro",
    ctaLabel: "Single-Held · 49 €/M",
    secondaryHref: "/campus/team",
    secondaryLabel: "Hauptquartier · 149 €",
    ctaTestId: "marketplace-cta-single-held",
    secondaryTestId: "marketplace-cta-hauptquartier",
    cardTestId: "marketplace-card-einrichtung",
    accent: "burgundy",
  },
];

export default function CampusMarketplace() {
  return (
    <section id="marktplatz" className="bg-cream-100 py-28 md:py-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Editorial header · asymmetric */}
        <Reveal>
          <div className="grid md:grid-cols-12 gap-6 items-end mb-16 md:mb-20">
            <div className="md:col-span-7">
              <div className="flex items-center gap-3 mb-4">
                <span className="h-px w-10 bg-terra" />
                <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-terra">
                  Jobs &amp; Personal · separat vom Campus-Abo
                </span>
              </div>
              <h2 className="font-magazine font-medium text-[2.6rem] sm:text-[3.6rem] lg:text-[4.4rem] text-sand-800 leading-[1.08]">
                Suchst du einen Job?<br />
                <span className="text-terra">Oder neues Personal?</span>
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="text-sand-600 text-[15px] leading-[1.7] max-w-sm md:ml-auto md:text-right">
                Im Dickies-Campus läuft ein eigener Pflege-Marktplatz — ohne
                Headhunter, ohne Provision. Beide finden sich direkt.
              </p>
            </div>
          </div>
        </Reveal>

        {/* Editorial diptych — no cards, just editorial columns with vertical divider */}
        <div className="grid md:grid-cols-2 md:divide-x md:divide-sand-300/60 gap-y-16 md:gap-y-0">
          {MARKET.map((m, idx) => {
            const accentColor = m.accent === "terra" ? "text-terra" : "text-brand-darkred";
            const accentBorder = m.accent === "terra" ? "border-terra" : "border-brand-darkred";
            return (
              <Reveal key={m.id} delay={idx * 140}>
                <div
                  data-testid={m.cardTestId}
                  className={`h-full flex flex-col ${idx === 0 ? "md:pr-10 lg:pr-14" : "md:pl-10 lg:pl-14"}`}
                >
                  {/* Editorial Nº label + kicker */}
                  <div className="flex items-baseline gap-4 mb-6">
                    <span className={`font-magazine text-[13px] italic ${accentColor}`}>
                      {m.number}
                    </span>
                    <span className="h-px flex-1 bg-sand-300/60" />
                  </div>
                  <div className={`text-[11px] uppercase tracking-[0.28em] font-semibold ${accentColor} mb-4`}>
                    {m.kicker}
                  </div>

                  <h3 className="font-magazine text-[1.6rem] md:text-[1.85rem] text-sand-800 font-normal leading-[1.15] max-w-md">
                    {m.headline}
                  </h3>

                  <p className="mt-5 text-sand-700 text-[14.5px] leading-[1.75] max-w-md">
                    {m.lede}
                  </p>

                  {/* Benefits · thin editorial list */}
                  <ul className="mt-8 space-y-3.5 flex-1 max-w-md">
                    {m.benefits.map((b, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-3 text-[14px] text-sand-700 leading-snug"
                      >
                        <span className={`mt-[9px] inline-block h-[3px] w-4 shrink-0 ${
                          m.accent === "terra" ? "bg-terra" : "bg-brand-darkred"
                        }`} />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA · editorial pill + optional secondary text-link */}
                  <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3">
                    <Link
                      to={m.ctaHref}
                      data-testid={m.ctaTestId}
                      className={`inline-flex items-center gap-2 rounded-full border-2 ${accentBorder} ${accentColor} h-12 px-6 text-[13px] font-semibold tracking-[0.06em] transition-all duration-300 group hover:bg-terra hover:border-terra hover:text-white ${
                        m.accent === "burgundy" ? "hover:bg-brand-darkred hover:border-brand-darkred" : ""
                      }`}
                    >
                      {m.ctaLabel}
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2} />
                    </Link>
                    {m.secondaryHref && (
                      <Link
                        to={m.secondaryHref}
                        data-testid={m.secondaryTestId}
                        className="text-[13px] font-semibold text-sand-800 border-b border-sand-400 hover:border-brand-darkred pb-0.5 transition-colors whitespace-nowrap"
                      >
                        {m.secondaryLabel}
                      </Link>
                    )}
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>

        {/* DSGVO strip — editorial footnote */}
        <Reveal delay={400}>
          <div className="mt-16 flex items-start gap-3 text-[13px] text-sand-600 max-w-2xl border-t border-sand-300/60 pt-6">
            <Shield className="h-4 w-4 text-terra mt-0.5 shrink-0" strokeWidth={1.8} />
            <span className="leading-relaxed">
              <strong className="text-sand-800 font-semibold">DSGVO-konform.</strong>{" "}
              Profile bleiben unsichtbar, bis die Pflegekraft sie aktiv freigibt. Kein
              Tracking, keine externen Headhunter.
            </span>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
